/**
 * Form Type Definitions
 * Interfaces for all form-related data structures and validation
 */

import { ServiceType, PeriodType, ConsumptionUnit, BagSize } from './api';
import { SelectOption, FormFieldProps } from './components';

// ============================================================================
// Authentication Form Types
// ============================================================================

export interface LoginForm {
  email: string;
  password: string;
}

export interface RegisterForm {
  name?: string;
  email: string;
  password: string;
  confirmPassword?: string;
  acceptTerms?: boolean;
}

export interface ForgotPasswordForm {
  email: string;
}

export interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
  token: string;
}

export interface DevLoginForm {
  username: string;
  password: string;
}

// ============================================================================
// Profile & Restaurant Setup Form Types
// ============================================================================

export interface RestaurantProfileForm {
  restaurant_name: string;
  country: string;
  city: string;
  cuisine_type: string;
  daily_covers: number;
  currency: string; // Auto-derived from country, but included for form handling
  address?: string | undefined;
  description?: string | undefined;
  website?: string | undefined;
  phone?: string | undefined;
}

// ============================================================================
// Supplier Form Types
// ============================================================================

export interface SupplierCreateForm {
  name: string;
  description?: string;
  has_certification: boolean;
  status: 'active' | 'inactive';
}

export interface SupplierUpdateForm {
  name?: string;
  description?: string;
  has_certification?: boolean;
  status?: 'active' | 'inactive';
}

// ============================================================================
// Covers Volume Form Types (Sales Data)
// ============================================================================

export interface CoversVolumeForm {
  period_start: string;
  period_end: string;
  covers_count: string | number;
  source?: string;
  // NEW FIELDS for AI scanning and categorization
  corrected_fields?: Record<string, unknown>;
  category?: string;
  shift?: string;
}

export interface UserProfileForm {
  name?: string;
  email: string;
  phone?: string;
  language?: string;
  timezone?: string;
  notifications_enabled?: boolean;
}

// ============================================================================
// Manual Entry Form Types
// ============================================================================

export interface ManualEntryForm {
  start_date: string;
  end_date: string;
  kwh_electricity?: number;
  m3_water?: number;
  m3_gas?: number;
  notes?: string;
}

export interface SingleConsumptionEntryForm {
  service_type: ServiceType;
  consumption: number;
  unit: string;
  start_date: string;
  end_date: string;
  cost?: number;
  currency?: string;
  notes?: string;
}

// ============================================================================
// Client & Customer Management Form Types
// ============================================================================

export interface ClientesForm {
  fecha: string;
  periodo: PeriodType;
  total_clientes: number;
  total_cubiertos: number;
  notas?: string;
}

export interface BulkClientEntryForm {
  entries: ClientesForm[];
  apply_to_all?: {
    periodo?: PeriodType;
    notas?: string;
  };
}

// ============================================================================
// Invoice Processing Form Types
// ============================================================================

export interface InvoiceUploadForm {
  file: File;
  service_type_hint?: ServiceType;
  expected_period?: {
    start_date?: string;
    end_date?: string;
  };
}

export interface InvoiceEditForm {
  service_type: ServiceType;
  consumption: number;
  consumption_unit: ConsumptionUnit;
  bag_size?: BagSize | undefined; // Required when consumption_unit is 'bolsas'
  start_date: string;
  end_date: string;
  cost_total?: number | undefined;
  currency: string;
  vendor_name?: string | undefined;
  invoice_number?: string | undefined;
  issued_date?: string | undefined;
  supply_id?: string | undefined;
  notes?: string | undefined;
}

// ============================================================================
// Search & Filter Form Types
// ============================================================================

export interface InvoiceSearchForm {
  service_type?: ServiceType;
  date_from?: string;
  date_to?: string;
  vendor_name?: string;
  min_consumption?: number;
  max_consumption?: number;
  min_cost?: number;
  max_cost?: number;
}

export interface ActivityFilterForm {
  service_type?: ServiceType | 'all';
  period?: 'week' | 'month' | 'quarter' | 'year';
  date_from?: string;
  date_to?: string;
}

export interface ReportGenerationForm {
  report_type: 'monthly' | 'quarterly' | 'annual' | 'custom';
  start_date: string;
  end_date: string;
  include_charts?: boolean;
  include_recommendations?: boolean;
  format: 'pdf' | 'excel' | 'csv';
  email_delivery?: boolean;
  recipient_email?: string;
}

// ============================================================================
// Settings & Configuration Form Types
// ============================================================================

export interface NotificationSettingsForm {
  email_notifications: boolean;
  sms_notifications?: boolean;
  weekly_summary: boolean;
  monthly_report: boolean;
  threshold_alerts: boolean;
  consumption_thresholds?: {
    electricity_kwh?: number;
    water_m3?: number;
    gas_m3?: number;
  };
}

export interface PreferencesForm {
  language: 'es' | 'en';
  timezone: string;
  currency: 'EUR' | 'USD' | 'MXN';
  date_format: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  number_format: 'european' | 'american';
  dashboard_layout?: 'compact' | 'detailed';
}

// ============================================================================
// Validation & Error Types
// ============================================================================

export interface FormFieldError {
  field: string;
  message: string;
  code?: string;
}

export interface FormValidationResult {
  isValid: boolean;
  errors: FormFieldError[];
  warnings?: FormFieldError[];
}

export interface FormState<T = Record<string, unknown>> {
  data: T;
  errors: Record<keyof T, string>;
  touched: Record<keyof T, boolean>;
  isSubmitting: boolean;
  isValid: boolean;
  isDirty: boolean;
}

// ============================================================================
// Form Option Types
// ============================================================================

// SelectOption moved to components.ts to avoid duplication

export interface CountryOption extends SelectOption {
  code: string;
  flag?: string;
}

export interface CuisineOption extends SelectOption {
  category?: string;
  popular?: boolean;
}

// Predefined options for common form fields
export const SERVICE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'electricidad', label: 'Electricidad' },
  { value: 'agua', label: 'Agua' },
  { value: 'gas', label: 'Gas' },
  { value: 'residuos-reciclables', label: 'Residuos Reciclables' },
  { value: 'residuos-organicos', label: 'Residuos Orgánicos' },
  { value: 'residuos-generales', label: 'Residuos Generales' },
];

// Service type to icon mapping
export const SERVICE_TYPE_ICONS = {
  electricidad: 'electricity' as const,
  agua: 'water' as const,
  gas: 'gas' as const,
  'residuos-reciclables': 'recycle' as const,
  'residuos-organicos': 'leaf' as const,
  'residuos-generales': 'trash' as const,
} as const;

export const PERIOD_TYPE_OPTIONS: SelectOption[] = [
  { value: 'diario', label: 'Diario' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'mensual', label: 'Mensual' },
];

/**
 * @deprecated Currency options will be removed as currency is now
 * automatically determined from user's country profile setting.
 */
export const CURRENCY_OPTIONS: SelectOption[] = [
  { value: 'EUR', label: '€ Euro' },
  { value: 'USD', label: '$ Dólar' },
  { value: 'MXN', label: '$ Peso Mexicano' },
  { value: 'GBP', label: '£ Libra Esterlina' },
  { value: 'CHF', label: 'CHF Franco Suizo' },
];

export const CUISINE_TYPE_OPTIONS: CuisineOption[] = [
  {
    value: 'mediterranea',
    label: 'Mediterránea',
    category: 'regional',
    popular: true,
  },
  {
    value: 'italiana',
    label: 'Italiana',
    category: 'internacional',
    popular: true,
  },
  { value: 'mexicana', label: 'Mexicana', category: 'regional', popular: true },
  { value: 'francesa', label: 'Francesa', category: 'internacional' },
  { value: 'japonesa', label: 'Japonesa', category: 'asiatica' },
  { value: 'china', label: 'China', category: 'asiatica' },
  { value: 'india', label: 'India', category: 'asiatica' },
  { value: 'americana', label: 'Americana', category: 'internacional' },
  { value: 'vegetariana', label: 'Vegetariana', category: 'especializada' },
  { value: 'vegana', label: 'Vegana', category: 'especializada' },
  { value: 'fusion', label: 'Fusión', category: 'moderna' },
  { value: 'tapas', label: 'Tapas', category: 'regional', popular: true },
  { value: 'mariscos', label: 'Mariscos', category: 'especializada' },
  { value: 'parrilla', label: 'Parrilla', category: 'especializada' },
  { value: 'cafeteria', label: 'Cafetería', category: 'casual' },
  { value: 'otro', label: 'Otro', category: 'otros' },
];

// ============================================================================
// Form Utility Types
// ============================================================================

export type FormMode = 'create' | 'edit' | 'view';

export interface FormProps<T = Record<string, unknown>> {
  mode: FormMode;
  initialData?: Partial<T>;
  onSubmit: (data: T) => Promise<void> | void;
  onCancel?: () => void;
  loading?: boolean;
  disabled?: boolean;
}

// FormFieldProps moved to components.ts to avoid duplication

// SelectFieldProps moved to components.ts to avoid duplication

export interface DateFieldProps extends FormFieldProps {
  minDate?: string;
  maxDate?: string;
  format?: string;
}

export interface NumberFieldProps extends FormFieldProps {
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

export interface FileFieldProps extends FormFieldProps {
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
}

// ============================================================================
// Form Validation Schema Types
// ============================================================================

export interface ValidationRule {
  required?: boolean | string;
  min?: number | string;
  max?: number | string;
  pattern?: RegExp | string;
  custom?: (value: unknown) => boolean | string;
}

export type ValidationSchema<T> = {
  [K in keyof T]?: ValidationRule;
};

// Common validation patterns
export const VALIDATION_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^[+]?[1-9][\d]{0,15}$/,
  postalCode: /^[0-9]{5}$/,
  website: /^https?:\/\/.+/,
  strongPassword:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
} as const;
